import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateReturn, useUpdateReturn } from '@/hooks/useReturns';
import type { Return as ReturnDoc, ReturnType, ReturnItem, CreateReturnRequest } from '@/types/returns';

interface ReturnDialogProps {
  isOpen: boolean;
  onClose: () => void;
  returnDoc?: ReturnDoc;
  defaultType?: ReturnType;
  defaultFlowId?: string;
  defaultSourcePOId?: string;
  defaultSourceSOId?: string;
}

const ReturnDialog: React.FC<ReturnDialogProps> = ({
  isOpen,
  onClose,
  returnDoc,
  defaultType = 'purchase',
  defaultFlowId,
  defaultSourcePOId,
  defaultSourceSOId,
}) => {
  const { t } = useTranslation('psi');
  const createReturn = useCreateReturn();
  const updateReturn = useUpdateReturn();

  const isEdit = !!returnDoc;

  const [returnType, setReturnType] = useState<ReturnType>(defaultType);
  const [returnDate, setReturnDate] = useState('');
  const [vendorCode, setVendorCode] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [currency, setCurrency] = useState('TWD');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ReturnItem[]>([
    { line_no: 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency: 'TWD', reason: '' },
  ]);

  useEffect(() => {
    if (returnDoc) {
      setReturnType(returnDoc.return_type);
      setReturnDate(returnDoc.return_date ? new Date(returnDoc.return_date).toISOString().split('T')[0] : '');
      setVendorCode(returnDoc.vendor_code || '');
      setCustomerCode(returnDoc.customer_code || '');
      setCurrency(returnDoc.currency || 'TWD');
      setNotes(returnDoc.notes || '');
      setItems(returnDoc.items?.length > 0 ? returnDoc.items : [
        { line_no: 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency: 'TWD', reason: '' },
      ]);
    } else {
      setReturnDate(new Date().toISOString().split('T')[0]);
      setReturnType(defaultType);
    }
  }, [returnDoc, defaultType]);

  const updateItem = (index: number, field: keyof ReturnItem, value: string | number) => {
    const newItems = [...items];
    (newItems[index] as unknown as Record<string, unknown>)[field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    setItems(newItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      { line_no: items.length + 1, product_name: '', specification: '', quantity: 1, unit_price: 0, amount: 0, currency, reason: '' },
    ]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const handleSubmit = async () => {
    if (isEdit && returnDoc) {
      await updateReturn.mutateAsync({
        id: returnDoc.return_id,
        request: {
          return_date: returnDate,
          vendor_code: vendorCode || undefined,
          customer_code: customerCode || undefined,
          items,
          currency,
          notes: notes || undefined,
        },
      });
    } else {
      const req: CreateReturnRequest = {
        return_type: returnType,
        return_date: returnDate,
        items,
        currency,
      };
      if (vendorCode) req.vendor_code = vendorCode;
      if (customerCode) req.customer_code = customerCode;
      if (defaultFlowId) req.flow_id = defaultFlowId;
      if (defaultSourcePOId) req.source_po_id = defaultSourcePOId;
      if (defaultSourceSOId) req.source_so_id = defaultSourceSOId;
      if (notes) req.notes = notes;
      await createReturn.mutateAsync(req);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? t('returns.dialog.editTitle', 'Edit Return') : t('returns.dialog.createTitle', 'Create Return')}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('returns.dialog.type', 'Type')}
              </label>
              <select
                value={returnType}
                onChange={(e) => setReturnType(e.target.value as ReturnType)}
                disabled={isEdit}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="purchase">Return to Vendor</option>
                <option value="sales">Return from Customer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('returns.dialog.date', 'Return Date')}
              </label>
              <input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {returnType === 'purchase' ? t('returns.dialog.vendor', 'Vendor Code') : t('returns.dialog.customer', 'Customer Code')}
            </label>
            <input
              type="text"
              value={returnType === 'purchase' ? vendorCode : customerCode}
              onChange={(e) => returnType === 'purchase' ? setVendorCode(e.target.value) : setCustomerCode(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('returns.dialog.items', 'Return Items')}
              </label>
              <button onClick={addItem} className="text-xs text-indigo-600 hover:text-indigo-700">
                + {t('returns.dialog.addItem', 'Add Item')}
              </button>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    type="text"
                    placeholder="Product Name"
                    value={item.product_name}
                    onChange={(e) => updateItem(idx, 'product_name', e.target.value)}
                    className="col-span-3 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="text"
                    placeholder="Reason"
                    value={item.reason || ''}
                    onChange={(e) => updateItem(idx, 'reason', e.target.value)}
                    className="col-span-3 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                    className="col-span-2 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <input
                    type="number"
                    placeholder="Price"
                    value={item.unit_price}
                    onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                    className="col-span-2 px-2 py-1.5 border rounded text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  <span className="col-span-1 text-sm text-right text-gray-600 dark:text-gray-400">
                    {(item.quantity * item.unit_price).toLocaleString()}
                  </span>
                  <button
                    onClick={() => removeItem(idx)}
                    className="col-span-1 text-red-400 hover:text-red-600 text-center"
                    disabled={items.length <= 1}
                  >
                    <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t dark:border-gray-700 pt-3 text-sm text-right font-semibold text-gray-900 dark:text-white">
            Total: {currency} {totalAmount.toLocaleString()}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('returns.dialog.notes', 'Notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={createReturn.isLoading || updateReturn.isLoading}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createReturn.isLoading || updateReturn.isLoading ? '...' : isEdit ? t('common.save', 'Save') : t('common.create', 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnDialog;
